import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import WF_MapSearchHeader from '../../common/mapSearchHeader/WF_MapSearchHeader';
import { LoadingSpinner } from '../../common/loading/LoadingSpinner';
import WF_Map from '../../common/map/WF_Map';
import { useWfWorkplaceStore } from 'src/api/wf/stores/WfWorkplaceStore';
import { useWfUserStore } from 'src/api/wf/stores/WfUserStore';
import WF_MapLegend from '../../common/map/WF_MapLegend';
import { useTranslation } from 'react-i18next';
import Input from '../../common/input/Input';
import Button from '../../common/button/Button';
import DataTable from 'react-data-table-component';
import { workerColumns, workerCustomStyles } from './WF_WorkerTableColumns';
import { useWfFilterStore } from 'src/api/wf/stores/WfFilterStore';
import useWfWorkplaceService from 'src/api/wf/service/WfWorkplaceService';
import { useWfLocationService } from 'src/api/wf/service/WfLocationService';
import { useNotify } from 'src/ui/common/alert/useNotify';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';
import { FormRow } from '../../common/form/FormRow';
import { defaultMapCenter, defaultMapZoom } from 'src/common/location/defaultMapSettings';

export const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

function WF_4_5_1_PropertiesMap() {
  const { t } = useTranslation();
  const userAgent = useUserAgent();
  const { availableWorkplaces, selectedWorkplace, setSelectedWorkplace, setAvailableWorkplaces } =
    useWfWorkplaceStore();
  const { createWorkplaceReservation, fetchWorkplaces } = useWfWorkplaceService();
  const { user, users, selectedUser, setSelectedUser } = useWfUserStore();
  const {
    mapCenter,
    setMapCenter,
    salesOfficeOptions,
    sectionOptions,
    setZoom,
    setUserOffice,
    filterParameters,
  } = useWfFilterStore();
  const { updateWfUserLocation } = useWfLocationService();
  const notify = useNotify();

  const [isSearched, setIsSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);
  const [recenterTrigger, setRecenterTrigger] = useState(0);

  // Ensure that the previous selection is not retained in the Proxy Reservation field when returning to this screen after navigating to Site details screen
  useEffect(() => {
    setSelectedWorkplace(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasMandatoryFilters =
      Boolean(filterParameters.branch) && Boolean(filterParameters.salesOffice);

    const hasCustomerFilter = Boolean(filterParameters.customerNumber);

    if ((hasMandatoryFilters || hasCustomerFilter) && !isSearched) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterParameters.branch,
    filterParameters.salesOffice,
    filterParameters.customerNumber,
    isSearched,
  ]);

  const handleSearch = async () => {
    setIsLoading(true);
    setIsTimeout(false);
    setAvailableWorkplaces([]);
    try {
      await fetchWorkplaces();
      setIsSearched(true);
      setRecenterTrigger((prev) => prev + 1);
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        setIsTimeout(true);
      } else {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleProxyReservation = async () => {
    if (!selectedWorkplace || !selectedUser) return;
    const reservationParams = {
      engineerUserId: selectedUser.id,
      branchCode: selectedWorkplace.branchCode,
      customerId: selectedWorkplace.customerId,
    };
    try {
      if (selectedWorkplace.details.reservations.length > 0) {
        notify('wf.searchHeader.reservationExists');
        return;
      }

      const response = await createWorkplaceReservation(reservationParams);

      if (response) {
        notify('wf.searchHeader.reservationSuccess');
        if (userAgent.isMobile) {
          updateWfUserLocation();
        }
      } else {
        notify('wf.searchHeader.reservationFailed');
      }
    } catch (error) {
      console.error('Error when trying to reserve with the data:', error);
      notify('wf.searchHeader.reservationFailed');
    } finally {
      setSelectedWorkplace(null);
      setSelectedUser(null);
    }
  };

  useEffect(() => {
    /**
     * Update map center location. In desktop "Map" screen, should follow following prority:
     * 0. Initially, Center on user's Sales Office (or Section)
     * 1. Searched with Organization, Selected "Centering on Sales Office" -> Center on Sales Office (or Section)
     * 2. Searched with Customer -> Center on Customer Location
     * 3. If in above cases, if the location data is not available, center on default location
     */
    // Case 0 and 1
    const hasCustomerFilter = Boolean(filterParameters.customerNumber);
    const salesOfficeCode = filterParameters.salesOffice;
    const sectionCode = filterParameters.section;
    const salesOffice = salesOfficeOptions.find((office) => office.code === salesOfficeCode);
    const section = sectionOptions.find((section) => section.code === sectionCode);
    const officeLocation = section?.location ?? salesOffice?.location ?? null;
    if (officeLocation && !hasCustomerFilter) {
      setMapCenter(officeLocation);
      setZoom(section?.defaultScale ?? salesOffice?.defaultScale ?? defaultMapZoom);
      setUserOffice(section?.location ? section : (salesOffice ?? null));
      return;
    }

    // Case 2
    const customerLocation = availableWorkplaces.find(
      (workplace) => workplace.customerId === filterParameters.customerNumber?.trim()
    )?.location;
    if (customerLocation) {
      setMapCenter(customerLocation);
      setZoom(defaultMapZoom);
      setUserOffice(null);
      return;
    }

    // Case 3
    setMapCenter(defaultMapCenter);
    setZoom(defaultMapZoom);
    setUserOffice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesOfficeOptions, sectionOptions, setMapCenter, availableWorkplaces, setZoom]);

  const sortedWorkers = useMemo(() => {
    const workers = [...users];
    if (mapCenter && mapCenter.latitude) {
      workers.sort((a, b) => {
        const hasA = a?.location?.latitude != null && a?.location?.longitude != null;
        const hasB = b?.location?.latitude != null && b?.location?.longitude != null;
        if (!hasA && !hasB) return 0;
        if (!hasA) return 1;
        if (!hasB) return -1;

        const distA = getDistance(
          mapCenter.latitude,
          mapCenter.longitude,
          a.location!.latitude,
          a.location!.longitude
        );
        const distB = getDistance(
          mapCenter.latitude,
          mapCenter.longitude,
          b.location!.latitude,
          b.location!.longitude
        );
        return distA - distB;
      });
    }
    return workers.map((worker, index) => ({ ...worker, displayIndex: index + 1 }));
  }, [users, mapCenter]);

  return (
    <div className="flex h-screen flex-col space-y-2">
      <div className="pt-2">
        <WF_MapSearchHeader onSearch={handleSearch} showNoResults={!isTimeout} />
      </div>
      {isTimeout && (
        <h3 className="mx-2 mb-2 text-center text-xl">{t('wf.common.searchTimeout')}</h3>
      )}
      {isLoading && (
        <div className="fixed inset-0 z-[9999]">
          <LoadingSpinner />
        </div>
      )}
      <div className="grid h-full grid-cols-10 gap-x-4 rounded-md border-t-2 border-sky-600 bg-gray-100 px-4 py-2">
        <div className="col-span-7 flex flex-col space-y-4">
          <WF_Map
            className="flex-1"
            workplaces={isSearched ? availableWorkplaces : []}
            workers={sortedWorkers}
            user={user}
            recenterTrigger={recenterTrigger}
          />
          <WF_MapLegend />
        </div>
        <div className="col-span-3 flex flex-col space-y-4">
          <div className="flex flex-col space-y-2 rounded border p-2">
            <h3 className="font-semibold">{t('wf.searchHeader.proxyReservation')}</h3>
            <FormRow title={t('wf.sites.map.buildingSite')}>
              <Input disabled value={selectedWorkplace?.customerId ?? ''} className="w-full" />
            </FormRow>
            <FormRow title={t('wf.searchHeader.maintenanceStaff')}>
              <Input disabled value={selectedUser ? `${selectedUser.id}` : ''} className="w-full" />
            </FormRow>
            <Button
              title={t('wf.searchHeader.proxyReservation')}
              onClick={handleProxyReservation}
              disabled={!selectedUser || !selectedWorkplace}
            />
          </div>
          <div className="flex flex-1 flex-col">
            <h3 className="font-semibold">{t('wf.sites.map.showFe')}</h3>
            <div className="w-full">
              <DataTable
                noDataComponent={t('wf.common.noResults')}
                noTableHead={true}
                columns={workerColumns(mapCenter)}
                data={sortedWorkers.slice(0, 20)}
                customStyles={workerCustomStyles}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WF_4_5_1_PropertiesMap;
